import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { animate, useReducedMotion } from 'framer-motion';
import { CountUp } from '../../components/aa';
import type { OrbitOverview, OrbitOverviewArea } from '../../types/aai';

/* 0 to 1 once on mount, so every chart on the Overview draws in together. */
export const useGrow = (duration = 0.9): number => {
  const reduce = useReducedMotion();
  const [t, setT] = useState(reduce ? 1 : 0);
  useEffect(() => {
    if (reduce) {
      setT(1);
      return undefined;
    }
    const controls = animate(0, 1, { duration, ease: [0.22, 1, 0.36, 1], onUpdate: setT });
    return () => controls.stop();
  }, [duration, reduce]);
  return t;
};

/* The donut is the coverage chart's cards added up, so the two always agree.
   "What could be automated" is left out: its cards are other areas' cards.
   A department head's areas are the company's, so their donut keeps the
   department's own counts. */
export const cardsOf = (overview: OrbitOverview, department = false): { known: number; confirm: number; open: number } => {
  if (department) return { known: overview.counts.known, confirm: overview.counts.confirm, open: overview.counts.open };
  const own = overview.areas.filter((a) => a.key !== 'opportunities');
  return {
    known: own.reduce((sum, a) => sum + a.known, 0),
    confirm: own.reduce((sum, a) => sum + a.confirm, 0),
    open: own.reduce((sum, a) => sum + a.ask, 0),
  };
};

const RING = 2 * Math.PI * 40;

export const MappedRing: React.FC<{ pct: number }> = ({ pct }) => {
  const t = useGrow();
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="dial" role="img" aria-label={`${clamped}% mapped`}>
      <svg viewBox="0 0 96 96" aria-hidden>
        <circle className="track" cx="48" cy="48" r="40" />
        <circle className="arc grow" cx="48" cy="48" r="40" style={{ strokeDasharray: RING, strokeDashoffset: RING * (1 - (clamped * t) / 100) }} />
      </svg>
      <span className="big"><CountUp value={clamped} suffix="%" /></span>
    </div>
  );
};

/* ── Coverage radar ─────────────────────────────────────────────────── */

const SIZE = 240;
const MID = SIZE / 2;
const RADIUS = 92;
const RINGS = [25, 50, 75, 100];

const pointAt = (i: number, n: number, pct: number): [number, number] => {
  const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
  const r = (RADIUS * pct) / 100;
  return [MID + r * Math.cos(angle), MID + r * Math.sin(angle)];
};

const polygon = (n: number, pctOf: (i: number) => number): string =>
  Array.from({ length: n }, (_, i) => pointAt(i, n, pctOf(i)).join(',')).join(' ');

export const CoverageRadar: React.FC<{ areas: OrbitOverviewArea[] }> = ({ areas }) => {
  const t = useGrow(1.1);
  const [hover, setHover] = useState<number>(-1);
  const n = areas.length;

  if (n < 3) {
    return (
      <div className="cov-list solo">
        {areas.map((a, i) => <CoverageRow key={a.key} area={a} index={i} on={hover === i} t={t} onHover={setHover} />)}
        <CoverageNote area={hover >= 0 ? areas[hover] : null} />
      </div>
    );
  }

  return (
    <div className="cov-viz">
      <div className="radar" onMouseLeave={() => setHover(-1)}>
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label="Coverage by area, as a radar">
          {RINGS.map((r) => (
            <polygon key={r} className="grid" points={polygon(n, () => r)} />
          ))}
          {areas.map((a, i) => {
            const [x, y] = pointAt(i, n, 100);
            return <line key={a.key} className={clsx('axis', hover === i && 'on')} x1={MID} y1={MID} x2={x} y2={y} />;
          })}
          <polygon className="shape" points={polygon(n, (i) => areas[i].pct * t)} />
          {areas.map((a, i) => {
            const [x, y] = pointAt(i, n, a.pct * t);
            if (a.pct === 0 && hover !== i) return null;
            return <circle key={a.key} className={clsx('dot', hover === i && 'on')} cx={x} cy={y} r={hover === i ? 5 : 4} />;
          })}
          {areas.map((a, i) => {
            const [x, y] = pointAt(i, n, 116);
            return (
              <g key={a.key} className={clsx('tag', hover === i && 'on')} onMouseEnter={() => setHover(i)}>
                <circle cx={x} cy={y} r={10} />
                <text x={x} y={y} dy="0.35em" textAnchor="middle">{i + 1}</text>
              </g>
            );
          })}
          {areas.map((a, i) => {
            const [x, y] = pointAt(i, n, 100);
            return (
              <line
                key={`hit-${a.key}`}
                className="hit"
                x1={MID}
                y1={MID}
                x2={x}
                y2={y}
                onMouseEnter={() => setHover(i)}
              />
            );
          })}
        </svg>
      </div>
      <div className="cov-list">
        {areas.map((a, i) => <CoverageRow key={a.key} area={a} index={i} on={hover === i} t={t} onHover={setHover} />)}
        <CoverageNote area={hover >= 0 ? areas[hover] : null} />
      </div>
    </div>
  );
};

/* "What could be automated" is drawn from the other maps: its known count is
   the opportunities named, its asks are the open "Where work piles up" questions. */
const breakdown = (area: OrbitOverviewArea): string => {
  if (area.key === 'opportunities') return `${area.known} named so far · waits on ${area.ask} open ${area.ask === 1 ? 'question' : 'questions'} about where work piles up`;
  return `${area.known} confirmed · ${area.confirm} waiting for a tap · ${area.ask} still open`;
};

const CoverageNote: React.FC<{ area: OrbitOverviewArea | null }> = ({ area }) => {
  if (!area) return <p className="cov-note">Hover an area to see its cards</p>;
  return <p className="cov-note on"><strong>{area.label}:</strong> {breakdown(area)}</p>;
};

const CoverageRow: React.FC<{ area: OrbitOverviewArea; index: number; on: boolean; t: number; onHover: (i: number) => void }> = ({ area, index, on, t, onHover }) => (
  <div
    className={clsx('cov-row', on && 'on')}
    onMouseEnter={() => onHover(index)}
    onMouseLeave={() => onHover(-1)}
  >
    <em>{index + 1}</em>
    <span>{area.label}</span>
    <b>{area.pct}%</b>
    <i><b style={{ width: `${area.pct * t}%` }} /></i>
  </div>
);

/* ── What Orbit knows ───────────────────────────────────────────────── */

const DONUT_R = 52;
const DONUT_C = 2 * Math.PI * DONUT_R;
/* A 2px surface gap between segments, in the same units as the circumference. */
const GAP = 2.5;

interface Slice { key: string; label: string; value: number; cls: string }

export const FactsDonut: React.FC<{ known: number; confirm: number; open: number; onAnswer?: () => void }> = ({ known, confirm, open, onAnswer }) => {
  const t = useGrow(1);
  const [hover, setHover] = useState('');
  const slices: Slice[] = [
    { key: 'known', label: 'Confirmed by a person', value: known, cls: 'known' },
    { key: 'confirm', label: 'Read, waiting for a tap', value: confirm, cls: 'confirm' },
    { key: 'open', label: 'Still open', value: open, cls: 'open' },
  ];
  const total = known + confirm + open;

  if (total === 0) {
    return <p className="facts-empty">Nothing on file yet. Give Orbit a website or a document and this fills in.</p>;
  }

  const shown = slices.filter((s) => s.value > 0);
  const gap = shown.length > 1 ? GAP : 0;
  let start = 0;
  const arcs = shown.map((s) => {
    const len = (s.value / total) * DONUT_C;
    const arc = { ...s, len: Math.max(0, len * t - gap), offset: -start * t };
    start += len;
    return arc;
  });
  const active = slices.find((s) => s.key === hover);
  let centerValue = total;
  let centerLabel = total === 1 ? 'card in all' : 'cards in all';
  if (active) {
    centerValue = active.value;
    centerLabel = active.label.split(',')[0].toLowerCase();
  }

  return (
    <div className="facts">
      <div className="donut" onMouseLeave={() => setHover('')}>
        <svg viewBox="0 0 128 128" role="img" aria-label={`${known} confirmed, ${confirm} waiting, ${open} open`}>
          <circle className="track" cx="64" cy="64" r={DONUT_R} />
          {arcs.map((a) => (
            <circle
              key={a.key}
              className={clsx('seg', a.cls, hover && hover !== a.key && 'dim')}
              cx="64"
              cy="64"
              r={DONUT_R}
              strokeDasharray={`${a.len} ${DONUT_C}`}
              strokeDashoffset={a.offset}
              onMouseEnter={() => setHover(a.key)}
            />
          ))}
        </svg>
        <div className="mid">
          <b>{active ? centerValue : <CountUp value={centerValue} />}</b>
          <small>{centerLabel}</small>
        </div>
      </div>
      <div className="legend">
        {slices.map((s) => (
          <div
            key={s.key}
            className={clsx('lg', hover === s.key && 'on')}
            onMouseEnter={() => setHover(s.key)}
            onMouseLeave={() => setHover('')}
          >
            <i className={s.cls} />
            <span>{s.label}</span>
            <b>{s.value}</b>
            <small>{Math.round((s.value / total) * 100)}%</small>
          </div>
        ))}
        {onAnswer && open > 0 && (
          <button type="button" className="answer" onClick={onAnswer}>Answer open cards</button>
        )}
      </div>
    </div>
  );
};
